import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useStickyState — a drop-in replacement for `useState` that persists the
 * value in `localStorage` so filters, tab picks, and sort choices survive
 * reloads.
 *
 * Namespaced per-user (via `scope`) so switching accounts on the same
 * browser doesn't leak the previous user's state.
 *
 * @param {string} key       Unique key for this piece of state
 * @param {*}      initial   Fallback value when nothing is stored yet
 * @param {string} scope     e.g. current user's email/id; changes wipe scope
 */
export function useStickyState(key, initial, scope = 'default') {
  const storageKey = `girgit:${scope}:${key}`;
  const isFirstRun = useRef(true);

  const [value, setValue] = useState(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw == null) return initial;
      return JSON.parse(raw);
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Skip the very first write — we already read from storage in useState.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* quota exceeded / private mode — persist silently fails */
    }
  }, [storageKey, value]);

  const reset = useCallback(() => setValue(initial), [initial]);

  return [value, setValue, reset];
}

export default useStickyState;
